export const ProjectHeadersService = {
  /**
   * Add project-specific headers to existing headers object
   * @param {Object} existingHeaders - Existing headers (typically from getAuthHeaders())
   * @param {Object} userData - User data from useUser hook containing org info
   * @param {Object} options - Additional options to override defaults
   * @param {string} options.tenantId - Override tenant ID
   * @param {string} options.bucketName - Override bucket name
   * @param {string} options.userId - Override user ID
   * @returns {Object} Enhanced headers object with project headers added
   */
  addProjectHeaders: (existingHeaders, userData, options = {}) => {
    const enhancedHeaders = { ...existingHeaders };

    // Ensure Accept header is present (required by API)
    if (!enhancedHeaders['Accept']) {
      enhancedHeaders['Accept'] = '*/*';
    }

    // Add tenant ID header
    const tenantId = options.tenantId || userData?.tenantId;
    if (tenantId) {
      enhancedHeaders['X-Tenant-Id'] = tenantId;
    }

    // Add division/organisation ID headers (required — HLD #4 division awareness)
    const divisionId = options.divisionId || userData?.divisionId;
    if (divisionId) {
      enhancedHeaders['X-Division-Id'] = divisionId;
    }

    const organisationId = options.organisationId || userData?.organisationId;
    if (organisationId) {
      enhancedHeaders['X-Organisation-Id'] = organisationId;
    }

    // Determine bucket name based on custom bucket configuration
    let bucketName = options.bucketName;
    
    if (!bucketName) {
      // Priority 1: Check userData.customBuckets flag (synced from backend)
      const customBucketsEnabled = userData?.customBuckets === true;
      const customBucketName = userData?.projectsBucketName;
      
      // Priority 2: Check currentOrganization.customBuckets (fallback)
      const orgCustomBucketsEnabled = userData?.currentOrganization?.customBuckets === true;
      const orgCustomBucketName = userData?.currentOrganization?.projectsBucketName;
      
      if (customBucketsEnabled && customBucketName) {
        // Use custom bucket from userData (highest priority)
        bucketName = customBucketName;
        console.log('📦 Using custom bucket (from userData):', bucketName);
      } else if (orgCustomBucketsEnabled && orgCustomBucketName) {
        // Use custom bucket from currentOrganization
        bucketName = orgCustomBucketName;
        console.log('📦 Using custom bucket (from currentOrganization):', bucketName);
      } else if (userData?.fileManagementBucket) {
        // Fallback to fileManagementBucket
        bucketName = userData.fileManagementBucket;
        console.log('📦 Using default bucket from fileManagementBucket:', bucketName);
      } else if (userData?.conversationsBucket) {
        // Fallback to genAI bucket
        bucketName = userData.conversationsBucket;
        console.log('📦 Using genAI bucket as fallback:', bucketName);
      }
    } else {
      console.log('📦 Using override bucket:', bucketName);
    }
    
    if (bucketName) {
      enhancedHeaders['X-Bucket-Name'] = bucketName;
    }

    // Add user ID header (required by file upload API)
    const userId = options.userId || userData?.userId;
    if (userId) {
      enhancedHeaders['X-User-Id'] = userId;
    } else if (userData?.accessToken) {
      // Only warn if user is actually logged in — during logout, userData is cleared
      console.warn('⚠️ X-User-Id not found in userData - file upload may fail');
    }

    return enhancedHeaders;
  },

  /**
   * Convenience method to get tenant ID from user data
   * @param {Object} userData - User data from useUser hook
   * @param {string} fallback - Fallback tenant ID if none found
   * @returns {string} Tenant ID
   */
  getTenantId: (userData, fallback = null) => {
    const tenantId = userData?.tenantId || 
                     userData?.currentOrganization?.tenantId || 
                     userData?.currentOrganization?.id;
    
    if (!tenantId && fallback) {
      console.warn('⚠️ No tenant ID found in userData, using fallback:', fallback);
    }
    
    return tenantId || fallback;
  },

  /**
   * Convenience method to get bucket name from user data
   * Checks for custom bucket first, then falls back to default
   * @param {Object} userData - User data from useUser hook
   * @param {string} fallback - Fallback bucket name if none found
   * @returns {string} Bucket name
   */
  getBucketName: (userData, fallback = null) => {
    // Check if organization has custom buckets configured
    const hasCustomBucket = userData?.currentOrganization?.customBuckets === true ||
                           userData?.currentOrganization?.projectsBucketName;
    
    let bucketName = null;
    
    if (hasCustomBucket && userData?.currentOrganization?.projectsBucketName) {
      // Use custom bucket
      bucketName = userData.currentOrganization.projectsBucketName;
      console.log('📦 getBucketName: Using custom bucket:', bucketName);
    } else if (userData?.fileManagementBucket) {
      // Fallback to fileManagementBucket from userData
      bucketName = userData.fileManagementBucket;
      console.log('📦 getBucketName: Using default bucket:', bucketName);
    } else if (userData?.conversationsBucket) {
      // Fallback to genAI bucket
      bucketName = userData.conversationsBucket;
      console.log('📦 getBucketName: Using genAI bucket as fallback:', bucketName);
    }
    
    if (!bucketName && fallback) {
      console.warn('⚠️ No bucket name found in userData, using fallback:', fallback);
      bucketName = fallback;
    }
    
    return bucketName;
  },

  /**
   * Check if organization is using custom buckets
   * @param {Object} userData - User data from useUser hook
   * @returns {boolean} True if using custom buckets
   */
  isUsingCustomBucket: (userData) => {
    const hasCustomBucket = userData?.currentOrganization?.customBuckets === true ||
                           userData?.currentOrganization?.projectsBucketName;
    
    return hasCustomBucket && !!userData?.currentOrganization?.projectsBucketName;
  },

  /**
   * Convenience method to get user ID from user data
   * @param {Object} userData - User data from useUser hook
   * @param {string} fallback - Fallback user ID if none found
   * @returns {string} User ID
   */
  getUserId: (userData, fallback = null) => {
    const userId = userData?.userId || 
                   userData?.id || 
                   userData?.user?.id ||
                   userData?.user?.userId;
    
    if (!userId && fallback) {
      console.warn('⚠️ No user ID found in userData, using fallback:', fallback);
    }
    
    return userId || fallback;
  },

  /**
   * Validate that required project headers can be added
   * @param {Object} userData - User data from useUser hook
   * @returns {Object} Validation result with missing fields
   */
  validateProjectHeaders: (userData) => {
    const validation = {
      isValid: true,
      missing: [],
      warnings: [],
      info: {}
    };

    const tenantId = ProjectHeadersService.getTenantId(userData);
    const bucketName = ProjectHeadersService.getBucketName(userData);
    const userId = ProjectHeadersService.getUserId(userData);
    const isCustomBucket = ProjectHeadersService.isUsingCustomBucket(userData);

    if (!tenantId) {
      validation.isValid = false;
      validation.missing.push('tenantId (for X-Tenant-ID)');
    }

    if (!bucketName) {
      validation.isValid = false;
      validation.missing.push('bucket name (for X-Bucket-Name)');
    }

    if (!userId) {
      validation.isValid = false;
      validation.missing.push('userId (for X-User-Id)');
    }

    // Add info about bucket configuration
    validation.info.tenantId = tenantId;
    validation.info.bucketName = bucketName;
    validation.info.userId = userId;
    validation.info.isCustomBucket = isCustomBucket;
    validation.info.bucketSource = isCustomBucket ? 'custom' : 'default';

    if (!validation.isValid) {
      validation.warnings.push('Some project headers may use fallback values');
    }

    return validation;
  }
};